<template>
    <main class="min-h-screen p-4 md:p-2">
        <article class="mx-auto max-w-7xl">
            <header class="w-full bg-hero text-gray-800 p-8 rounded-lg">
                <UBreadcrumb :links="breadcrumbs" divider="/"
                    :ui="{ active: 'text-slate-800 active', li: 'text-slate-500 gap-2', ol: 'flex-wrap', base: 'font-normal', label: 'label', inactive: 'hover:text-slate-800 hover:underline' }" />
                <div class="mt-8 flex justify-between gap-8 flex-col md:flex-row">
                    <div class="flex flex-col">
                        <div class="text-sm flex gap-2 flex-wrap">
                            <span
                                class="rounded-full border-2 border-slate-500 text-slate-700 p-1 px-4 flex gap-1 items-center">
                                <Icon name="heroicons:bolt-20-solid" /> App
                                Templates
                            </span>
                            <span class="rounded-full border-2 border-slate-500 text-slate-700 p-1 px-4"
                                v-for="tag in page.article.tags">{{ tag }}</span>
                        </div>
                        <h1 class="mt-8 text-2xl md:text-4xl leading-[3rem] font-bold text-pretty"> {{ page.title }}
                        </h1>
                        <div class="my-4 md:my-8">
                            <span class="text-slate-700 font-semibold">Get updates on Upcoming App Templates and
                                Blogs</span>
                            <NewsletterInput class="max-w-max text-white mt-2" submitText="Subscribe" />
                        </div>

                    </div>

                    <div class="max-w-[30rem] lg:block">
                        <img :src="page.thumbnail ?? page.article.thumbnail" class="rounded-md" />

                    </div>
                </div>
                <div class="flex mt-4 items-center justify-between flex-wrap gap-4">
                    <div class="flex gap-4 items-center flex-wrap">
                        <div class="flex items-center gap-2">
                            <img :src="page.author.img" class="w-8 h-8 object-cover rounded-full" />
                            <span class="font-semibold">{{ page.author.name }}</span>
                        </div>
                        <span class="text-slate-700 font-semibold">·</span>
                        <span class="text-slate-700">Published {{ articleDate }}.</span>
                        <span class="text-slate-700">Updated {{ articleDate }}</span>
                        <span class="text-slate-700 font-semibold">·</span>
                        <span class="text-slate-700">{{ calcReadingTime() }} min read</span>
                    </div>
                    <div>
                        <ArticleShare :href="currentUrl" :pure="true"
                            linkClass="text-slate-700 border-slate-600 hover:border-slate-900 hover:text-slate-900 focus:border-slate-900 focus:text-slate-900" />
                    </div>
                </div>
            </header>
            <div>
                <div class="flex gap-8 relative justify-start max-w-7xl">
                    <aside
                        class="mt-8 sticky top-header left-0 h-full flex flex-col gap-8 flex-none w-[20rem] hidden md:flex">
                        <div class="bg-[#F1F1F2]  p-4 rounded-xl w-full max-h-[20rem] overflow-y-scroll">
                            <span class="font-serif font-bold pw-text-black">More Resources</span>
                            <DocsDevAside />
                            <!-- <DocsTocBlog :links="toc.links" :onlyLinks="true" class="mt-2" /> -->
                        </div>
                        <div class="flex flex-col p-4 bg-pathway rounded-xl text-white">
                            <span class="font-bold">Pathway's Newsletter</span>
                            <span class="text-sm mt-2">Receive updates on new templates, engineering blog posts, and the
                                latest in the world of GenAI & Realtime AI.</span>
                            <NewsletterInput class="mt-4 text-sm" />
                        </div>
                        <div class="flex flex-col items-start border-t pt-4">
                            <span class="text-sm font-bold mb-2">Share this article</span>
                            <ArticleShare :href="currentUrl" :pure="true" />
                        </div>
                    </aside>
                    <section class="mt-4 grow min-w-0">
                        <ArticleTop :article="article" :page="page" :buttons="buttons"
                            :deployButtons="page?.deployButtons" />
                        <div ref="articleContent">
                            <slot />
                        </div>
                        <hr />
                        <div class="flex justify-center gap-8 flex-wrap items-center">
                            <Author :author="page?.author" v-if="page?.author" />
                            <Author :author="page?.author?.coauthors" v-if="page?.author && page?.author.coauthors" />
                        </div>
                    </section>
                </div>
                <FancyBanner class="mt-8" logoIcon="heroicons:bolt-20-solid">
                    <template #default>
                        <a href="/developers/user-guide/introduction/installation"
                            class="p-4 bg-primary-500 text-white mt-2 flex gap-2 items-center max-w-max rounded-lg font-bold">
                            Get started for free
                            <Icon name="heroicons:arrow-right-16-solid" />
                        </a>
                    </template>
                    <template #logoText>
                        Power your RAG and ETL pipelines with Live Data
                    </template>
                </FancyBanner>
                <div v-if="related.length" class="overflow-x-auto w-full mx-auto p-4 mt-8">
                    <span class="text-2xl font-serif font-bold">Related Articles</span>
                    <ul class="grid grid-cols-[repeat(auto-fill,minmax(19rem,1fr))] gap-8 mt-8 flex-wrap">
                        <li v-for="article in related" class="h-full">
                            <ArticleTile :article="article" class="h-full max-w-[25rem]" />
                        </li>
                    </ul>
                </div>

                <DocsPrevNext class="mb-8" />
                <!-- <ArticleFooter :page="page" :article="page.article" :related="related" :currentUrl="currentUrl"
                    :hasComments="hasComments" class="mt-8" /> -->
            </div>
        </article>
    </main>
</template>

<script setup>
const { page, toc } = useContent()
const related = await getRelatedArticles(page.value) ?? []
const currentUrl = computed(() => process.client ? window.location.href : 'https://pathway.com' + page.value?._path)
const article = computed(() => page.value?.article)

const articleDate = new Date(page.value?.date ?? page.value?.article?.date)?.toLocaleString('en-EN', {
    month: 'long', day: 'numeric',
    year: 'numeric'
}) ?? ''


const articleContent = ref(null)
function calcReadingTime () {
    const wordsPerMinute = 240
    const noOfWords = articleContent.value?.textContent?.split(/\s/g)?.length || 0
    const minutes = noOfWords / wordsPerMinute
    const readTime = Math.ceil(minutes)
    return readTime
}

/* Top buttons logic */
const buttons = computed(() => {
    const result = {}
    if (page.value?.notebook_export_path) {
        result.notebook = "https://github.com/pathwaycom/pathway/blob/main/examples/" + page.value.notebook_export_path
        result.colab = "https://colab.research.google.com/github/pathwaycom/pathway/blob/main/examples/" + page.value.notebook_export_path
        result.studio = "https://lightning.ai/new?repo_url=" + result.notebook
    }
    if (page.value?.notebook_github_link) {
        result.notebook = "https://github.com/pathwaycom/pathway/blob/main/examples/" + page.value.notebook_github_link
        result.colab = "https://colab.research.google.com/github/pathwaycom/pathway/blob/main/examples/" + page.value.notebook_github_link
        result.studio = "https://lightning.ai/new?repo_url=" + result.notebook
    }
    if (page.value?.project_github_link) {
        result.project = page.value?.project_github_link
    }
    if (page.value?.docker_github_link) {
        result.docker = page.value?.docker_github_link
    }

    return result
})

const breadcrumbs = [{
    label: 'Templates',
    to: '/developers/templates'
}, {
    label: page.value.title,
}]
</script>

<style scoped lang="postcss">
.bg-hero {
    background: #141d2d;
    background: linear-gradient(40deg, #D5DEEC 12%, #f1f1f1 50%, #DFE5EF)
}

.bg-pathway {
    background: #141d2d;
    background: linear-gradient(90deg, #141d2d 42%, #152033 50%, #2729b0)
}

::v-deep() {
    .label {
        overflow: visible;
    }

    .active .label {
        overflow: hidden;
        text-overflow: ellipsis;
    }
}
</style>
